import type { IServerMessageBodyNoticeUserNewInfo, IServerMessageBodyNoticeUserLeave } from "@tencent/docs-connection";
import uniqBy from "lodash/uniqBy";
import throttle from "lodash/throttle";

/** 房间链接 */
interface Connections {
    /** 后端生成的会话唯一id（toid+时间戳） */
    uuId: string;
    /** rename to uid */
    uid: string;
    isGuest: boolean;
}

export interface IUserInfo {
    uid: string;
    name: string;
    userPic: string;
}

/** 查询接口返回的用户信息  */
export interface IUserInfoServer {
    reqUid: string;
    uid: string;

    // 昵称、头像 均为 optional，因为游客状态没有权限获取；
    markName?: string;
    userPic?: string;
}

/** [uid 标记] 游客，即未登录 */
const GUEST_TOKEN = "g.";
/** [uid 标记] 参与者 */
const PARTICIPANT_TOKEN = "p.";

/**
 * 处理 socket 用户链接进出消息，批量请求用户信息
 */
class UserManager {
    /** convent userId to uid; e.g. 'g.10086' -> '10086' */
    static getRealUid = (userId: string) => userId.replace(GUEST_TOKEN, "").replace(PARTICIPANT_TOKEN, "");

    static isGuest = (userId: string) => userId.startsWith(GUEST_TOKEN);

    /** 登录用户列表 */
    public loginedUsers: IUserInfo[] = [];
    /** 用户总数（登录用户+游客），其中登录用户多开只记一次 */
    public userCount: number = 0;

    private connections = new Map<string /** uuid */, Connections>();
    private pendingQueue: Set<string /** uid */> = new Set();
    private requestingQueue: Set<string /** uid */> = new Set();
    /** userInfo 接口数据缓存 */
    private requestCache = new Map<string /** uid */, IUserInfo>();
    /** 用户本人的 userId */
    private selfUserId?: string;
    private isSelfGuest = false;

    public constructor(
        private getUserInfos: (uids: string[]) => Promise<IUserInfoServer[] | false>,
        /** NOTE: 非实时 */
        private onUserChange: () => void
    ) {}

    public setSelfUserId(userId: string) {
        this.selfUserId = userId;
        this.isSelfGuest = UserManager.isGuest(this.selfUserId);
    }

    public handleUserEnter(msg: IServerMessageBodyNoticeUserNewInfo) {
        const uid = UserManager.getRealUid(msg.userId);
        const isGuest = UserManager.isGuest(msg.userId);
        this.connections.set(msg.uuId, {
            uuId: msg.uuId,
            uid,
            isGuest,
        });

        // userinfo 接口需要鉴权，用户本人未登录跳过拉取
        if (!this.isSelfGuest) {
            if(!isGuest && !this.requestCache.has(uid)) {
                this.addUidToRequestQueue(uid);
            }
        }

        this.rebuildUserList();
    }

    public handleUserLeave(msg: IServerMessageBodyNoticeUserLeave) {
        this.connections.delete(msg.uuId);
        this.rebuildUserList();
    }

    private rebuildUserList = throttle(
        () => {
            const uniqueUsers = uniqBy([...this.connections.values()], "uid") as Connections[];

            this.loginedUsers = uniqueUsers.reduce((list, u) => {
                if (this.requestCache.has(u.uid)) list.push(this.requestCache.get(u.uid)!);
                return list;
            }, [] as IUserInfo[]);
            this.userCount = uniqueUsers.length;

            this.onUserChange();
        },
        500,
        {
            leading: false,
            trailing: true,
        }
    );

    private addUidToRequestQueue(uid: string) {
        if (this.pendingQueue.has(uid) || this.requestingQueue.has(uid)) return;

        this.pendingQueue.add(uid);
        this.batchRequestUserInfoAndCache();
    }

    // 失败不重试
    private batchRequestUserInfoAndCache = throttle(
        async () => {
            if (this.pendingQueue.size === 0) return;

            const uids = [...this.pendingQueue];
            this.pendingQueue.clear();

            uids.forEach(uid => this.requestingQueue.add(uid));
            const resp = await this.getUserInfos(uids);
            uids.forEach(uid => this.requestingQueue.delete(uid));

            if (!resp || resp.length === 0) return;

            resp.forEach((info) => {
                this.requestCache.set(info.reqUid, {
                    uid: info.reqUid,
                    name: info.markName ?? "",
                    userPic: info.userPic ?? "",
                });
            });

            this.rebuildUserList();
        },
        1000,
        { leading: false, trailing: true }
    );
}

export default UserManager;
